import React, { useState, useEffect } from 'react';

interface ToastProps {
    message: string;
    type: 'success' | 'info' | 'warning' | 'error';
    duration?: number;
    onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({ message, type, duration = 3000, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, onClose]);

    const getToastStyle = () => {
        switch (type) {
            case 'success':
                return 'bg-green-500 text-white';
            case 'info':
                return 'bg-blue-500 text-white';
            case 'warning':
                return 'bg-yellow-500 text-black';
            case 'error':
                return 'bg-red-500 text-white';
            default:
                return 'bg-gray-500 text-white';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return '✓';
            case 'info':
                return 'ⓘ';
            case 'warning':
                return '⚠';
            case 'error':
                return '✕';
            default:
                return 'ⓘ';
        }
    };

    return (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ease-in-out transform translate-x-0 ${getToastStyle()}`}>
            <span className="mr-2 text-lg">{getIcon()}</span>
            <span className="text-sm font-medium">{message}</span>
            <button
                onClick={onClose}
                className="ml-3 text-white hover:text-gray-200 transition-colors duration-200"
            >
                <span className="text-lg">×</span>
            </button>
        </div>
    );
};

interface ToastContextType {
    showToast: (message: string, type?: 'success' | 'info' | 'warning' | 'error') => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Array<{ id: number; message: string; type: 'success' | 'info' | 'warning' | 'error' }>>([]);

    const showToast = (message: string, type: 'success' | 'info' | 'warning' | 'error' = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
    };

    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <div className="fixed bottom-0 right-0 p-4 z-50 space-y-2">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = React.useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
